import type { PaymentGatewayKey } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Checkbox, RadioGroup } from "@sagot/ui";
import { useId, useMemo, useState } from "react";
import { gatewayChoices } from "./billing-model";

/** Body fields sent with every customer-present payment (`CheckoutChoiceIn`, DEC-042). */
export interface CheckoutChoiceBody {
  gateway?: PaymentGatewayKey;
  save_method: boolean;
}

export interface GatewayChoiceState {
  choices: ReturnType<typeof gatewayChoices>;
  gateway: PaymentGatewayKey | null;
  setGateway: (gateway: PaymentGatewayKey) => void;
  saveMethod: boolean;
  setSaveMethod: (save: boolean) => void;
  body: CheckoutChoiceBody;
}

/** Payer's gateway (first available by default) and consent to keep the method for renewals. */
export function useGatewayChoice(gateways: readonly string[]): GatewayChoiceState {
  const choices = useMemo(() => gatewayChoices(gateways), [gateways]);
  const [picked, setGateway] = useState<PaymentGatewayKey | null>(null);
  const [saveMethod, setSaveMethod] = useState(true);
  const gateway = picked && choices.some((c) => c.gateway === picked) ? picked : (choices[0]?.gateway ?? null);
  const body: CheckoutChoiceBody = gateway ? { gateway, save_method: saveMethod } : { save_method: saveMethod };
  return { choices, gateway, setGateway, saveMethod, setSaveMethod, body };
}

/**
 * "Pay with" for USD (Card via Stripe or PayPal) + "Keep this method for renewals". PHP has only
 * PayMongo, so nothing to choose; PayMongo methods are saved in-app, not through checkout.
 */
export function GatewayChooser({ state }: { state: GatewayChoiceState }) {
  const { t } = useTranslation();
  const legendId = useId();
  const hosted = state.gateway === "stripe" || state.gateway === "paypal";
  if (state.choices.length <= 1 && !hosted) return null;
  return (
    <div className="space-y-3 rounded-md border border-line p-3" data-testid="gateway-chooser">
      {state.choices.length > 1 ? (
        <div>
          <p id={legendId} className="mb-2 text-xs font-semibold uppercase tracking-wide text-fg-subtle">
            {t("billing.gateway.legend")}
          </p>
          <RadioGroup
            aria-labelledby={legendId}
            variant="cards"
            className="sm:grid-cols-2"
            value={state.gateway ?? state.choices[0]?.gateway ?? "stripe"}
            onValueChange={state.setGateway}
            options={state.choices.map((choice) => ({ value: choice.gateway, label: t(`billing.gateway.${choice.labelKey}`), description: t(`billing.gateway.hint_${choice.labelKey}`) }))}
          />
        </div>
      ) : null}
      {hosted ? (
        <Checkbox
          checked={state.saveMethod}
          onCheckedChange={(checked) => { state.setSaveMethod(checked === true); }}
          label={t("billing.gateway.save")}
          description={t("billing.gateway.saveHint")}
        />
      ) : null}
    </div>
  );
}
